import { reactive } from "vue";

/**
 * @typedef {Object} ListFieldColumn
 * @property {string} key - Property name on each item; `"value"` when the list holds a single
 *   value per row instead of an object (e.g. a plain string).
 * @property {string} inputTitle - Label shown above this column's "add new item" input.
 * @property {string} columnLabel - Table header text for this column.
 * @property {string} [placeholder] - Placeholder shown in this column's "add new item" input.
 * @property {boolean} [optional] - When true, this field doesn't gate `add()` — the row can be
 *   added with it left blank (e.g. a custom field's optional trait name).
 */

/**
 * @typedef {Object} ListFieldDescriptor
 * @property {string} key - Identifies this list (matches the descriptor lookup key, not
 *   necessarily a `flags` key — see `items` below).
 * @property {string} addButtonLabel - Label for the "add" button.
 * @property {ListFieldColumn[]} fields - One entry for a plain-value list, several for a list of
 *   objects.
 */

/**
 * Drives one add-a-row/delete-a-row list editor (Traits Whitelist, Coupon Override, Custom
 * Fields, …) against an arbitrary array ref, generalized over plain-value lists (a single field
 * named `"value"`) and lists of objects (several named fields) so one component renders every
 * such CRUD table.
 *
 * `items` decouples the editing UI from where the array actually lives: pass a writable computed
 * wrapping `flags[key]` for a list that *is* the persisted value, or a plain `ref([...])` for one
 * that's assembled into a different shape at save time (e.g. custom-fields rows collapsed into an
 * object keyed by field name).
 *
 * Call once per descriptor in `setup()` — the returned object holds the in-progress "new item"
 * as live state, so calling this again on every render (e.g. from inside a template `v-for`)
 * would reset whatever the user is currently typing.
 *
 * @param {import("vue").Ref<any[]>} items - Ref (or writable computed) holding the array.
 * @param {ListFieldDescriptor} descriptor - Describes the list's shape.
 * @returns {{
 *   newItem: *,
 *   items: any[],
 *   canAdd: boolean,
 *   add: () => void,
 *   remove: (index: number) => void,
 * }} Reactive controller — `newItem`/`items`/`canAdd` read live, `add`/`remove` mutate `items`
 *   directly. `canAdd` mirrors the same required-fields check `add()` gates on, so the UI can
 *   disable the add button (with an explanatory message) before the user even clicks it.
 * @example
 * const traitsWhitelist = useListField(
 *   computed({ get: () => flags.traits_whitelist, set: v => (flags.traits_whitelist = v) }),
 *   {
 *     key: "traits_whitelist",
 *     addButtonLabel: "Add Trait",
 *     fields: [{ key: "value", inputTitle: "Add New Trait", columnLabel: "Trait" }],
 *   },
 * );
 * traitsWhitelist.newItem = "some_trait";
 * traitsWhitelist.add(); // flags.traits_whitelist === ["some_trait"]
 */
export const useListField = (items, descriptor) => {
  const isPlainValue = descriptor.fields.length === 1 && descriptor.fields[0].key === "value";
  const blankItem = () =>
    isPlainValue ? null : Object.fromEntries(descriptor.fields.map(field => [field.key, ""]));
  // Trim before checking so a whitespace-only value doesn't pass as "filled" — it would otherwise
  // get added to the list here and then silently dropped by trimming at save time instead.
  const isNonBlank = value => Boolean(typeof value === "string" ? value.trim() : value);
  const isFilled = item =>
    isPlainValue
      ? isNonBlank(item)
      : descriptor.fields
          .filter(field => !field.optional)
          .every(field => isNonBlank(item[field.key]));

  const controller = reactive({
    newItem: blankItem(),
    get items() {
      return items.value || [];
    },
    get canAdd() {
      return isFilled(controller.newItem);
    },
    add: () => {
      if (!controller.canAdd) return;
      if (!items.value) items.value = [];
      items.value.push(controller.newItem);
      controller.newItem = blankItem();
    },
    remove: index => items.value.splice(index, 1),
  });

  return controller;
};
